import { Injectable, SecurityContext } from '@angular/core';
import { first, map, skipWhile } from 'rxjs/operators';
import { AuthenticationState, AuthInfo } from '../authentication/authentication-state';
import { Observable } from 'rxjs';
import { LinkByFile, LinkByDocument } from '../documents';
import { Settings } from '../core/settings';
import { ActivityLogger } from '../activities/activity-logger';
import { DomSanitizer } from '@angular/platform-browser';

@Injectable()
export class ELearningOpener {

    private readonly $token: Observable<string>;

    constructor(
        private readonly authenticationState: AuthenticationState,
        private readonly settings: Settings,
        private sanitizer: DomSanitizer,
        private readonly activityLogger: ActivityLogger,
    ) {
        this.$token = this.authenticationState.authInfo
            .pipe(
                skipWhile((x: AuthInfo) => !x.isAuthenticated),
                first(),
                map(x => x.accessToken.accessToken)
            );
    }

    public async openFromNotification(link: LinkByDocument): Promise<boolean> {
        this.activityLogger.logDownloadActivity(link);

        const documentIdInput = this.createInputElement('documentId', link.documentId);
        const categoryIdInput = this.createInputElement('categoryId', link.categoryNaturalKey);
        return await this.submit([documentIdInput, categoryIdInput], false);
    }

    public async open(link: LinkByFile): Promise<boolean> {
        this.activityLogger.logDownloadActivity(link);

        const documentIdInput = this.createInputElement('documentId', link.reportId);
        const documentLanguageIdInput = this.createInputElement('documentLanguageId', link.fileId);
        const categoryIdInput = this.createInputElement('categoryId', link.categoryNaturalKey);
        return await this.submit([documentIdInput, documentLanguageIdInput, categoryIdInput], true);
    }

    private async submit(formElements: HTMLInputElement[], byLanguageId: boolean): Promise<boolean> {
        return this.$token.pipe(
            map((token: string) => {
                const form = document.createElement('form');
                form.action = `${this.settings.apiUrl}/static-content/e-learning${byLanguageId ? '' : '/notification-link'}`;
                form.method = 'POST';
                form.target = '_blank';

                const authInput = this.createInputElement(this.settings.authenticationTokenPostFieldName, token);
                form.appendChild(authInput);
               
                for (var el of formElements) {
                    form.appendChild(el);
                }
                const parser = new DOMParser();
                const html = parser.parseFromString(this.sanitizer.sanitize(SecurityContext.HTML, form.innerHTML), 'text/html');   

                document.body.appendChild(form);
                form.submit();
                document.body.removeChild(form);

                return true;
            })
        ).toPromise<boolean>();
    }

    private createInputElement(name: string, value: string): HTMLInputElement {
        const element = document.createElement('input');
        element.name = name;
        element.type = 'hidden';
        element.value = value;
        return element;
    }
}
